import {
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  FormInstance,
  Input,
  message,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Table,
  Typography
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { createApiKey, deleteApiKey, getApiKeys, refreshApiKey, updateApiKey } from "api/apiKeys";
import { LoadingSpinner } from "components/LoadingSpinner";
import { getPaginationProps } from "components/PaginationProps";
import { SearchComponent } from "components/SearchComponent";
import { useEffect, useRef, useState } from "react";
import classes from "./APIKeys.module.css";
import { ShowApiKeyModal } from "./modal/ShowApiKey";
import cancelIcon from "assets/cancel.png";
import { AddCreditsForUserModal } from "modals/AddCreditsForUserModal/AddCreditsForUserModal";
import { User } from "types/props";

interface ApiKey {
  id: string;
  key: string;
  clientEmail: string;
  clientName: string;
  isBlocked: boolean;
  createdAt: string;
  user?: User;
}

export const AdminAPIKeys: React.FC = () => {
  const [modal1Open, setModal1Open] = useState(false);
  const [tableData, setTableData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showKeyModal, setShowKeyModal] = useState<boolean>(false);
  const [currentApiKey, setCurrentApiKey] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [addCreditsModal, setAddCreditsModal] = useState(false);

  const [selected, setSelected] = useState<
    Pick<ApiKey, "id" | "clientName" | "clientEmail" | "isBlocked">
  >({
    id: "",
    clientName: "",
    clientEmail: "",
    isBlocked: false
  });

  const handleDelete = async (id: string) => {
    setIsLoading(true);
    const res = await deleteApiKey(id);
    if (res instanceof Array && res.length === 0) {
      return;
    }
    await fetchAPIKeys();
    message.success("Api key deleted successfully!");
  };

  const form = useRef<FormInstance>(null);

  const columns: ColumnsType<ApiKey> = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      align: "center"
    },
    {
      title: "Client Email",
      key: "clientEmail",
      dataIndex: "clientEmail",
      align: "center"
    },
    {
      title: "Blocked",
      dataIndex: "isBlocked",
      key: "isBlocked",
      align: "center",
      render(value, record, index) {
        return value ? "Yes" : "No";
      },
      filters: [
        {
          text: "Yes",
          value: true
        },
        {
          text: "No",
          value: false
        }
      ],
      onFilter: (value, record) => record.isBlocked === value
    },
    {
      title: "Free Credits",
      key: "freeCredits",
      dataIndex: "user.freeCredits",
      align: "center",
      render(value, record, index) {
        return record.user?.freeCredits;
      }
    },
    {
      title: "Credits",
      key: "credits",
      dataIndex: "user.credits",
      align: "center",
      render(value, record, index) {
        return record.user?.credits;
      }
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render(value, record, index) {
        return new Date(value).toLocaleDateString() + " " + new Date(value).toLocaleTimeString();
      }
    },
    {
      title: "Action",
      key: "action",
      align: "left",
      width: 150,
      render: (record: ApiKey) => (
        <>
          <EditOutlined
            onClick={() => {
              setSelected({
                id: record.id,
                clientName: record.clientName,
                clientEmail: record.clientEmail,
                isBlocked: record.isBlocked
              });
              setModal1Open(true);
              setIsLoading(false);
            }}
            className={classes.EditButton}
          />
          <Popconfirm
            trigger={"click"}
            title={"Do you want to regenerate the API key?"}
            description={"The old API key will be invalidated"}
            okText={"Yes"}
            cancelText={"No"}
            onConfirm={async () => {
              const res = await refreshApiKey(record.id);
              if (!res) return;
              message.success("API key updated!");
              setShowKeyModal(true);
              setCurrentApiKey(res.key);
            }}>
            <ReloadOutlined className={classes.RefreshButton} />
          </Popconfirm>
          <Popconfirm
            title={"Are you sure you want to delete this API key?"}
            okText={"Yes"}
            cancelText={"No"}
            onConfirm={async () => await handleDelete(record.id)}>
            <DeleteOutlined className={classes.DeleteButton} />
          </Popconfirm>
        </>
      )
    }
  ];

  const fetchAPIKeys = async () => {
    try {
      const res = await getApiKeys();

      const data = res?.data?.data?.map((item: any) => ({
        id: item.id,
        clientEmail: item.clientEmail,
        clientName: item.clientName,
        isBlocked: item.isBlocked,
        createdAt: item.createdAt,
        user: item.user
      }));
      setTableData(data);
    } catch (error) {
      // console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div>
        <Typography.Title style={{ fontWeight: "normal", fontSize: "30px" }} level={1}>
          API Keys
        </Typography.Title>
        <SearchComponent
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder="Search by name, email">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginLeft: 8
            }}>
            <Button
              onClick={() => setAddCreditsModal(true)}
              style={{ border: "none", height: "40px", marginRight: 12 }}>
              Add Credits
            </Button>
            <Button
              style={{ height: "40px", border: "none" }}
              onClick={() => {
                setIsModalOpen(true);
              }}>
              <PlusOutlined />
              Create API Key
            </Button>
          </div>
        </SearchComponent>

        <Table
          columns={columns}
          dataSource={tableData.filter((apiKey: any) => {
            return (
              apiKey.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              apiKey.clientEmail?.toLowerCase().includes(searchQuery.toLowerCase())
            );
          })}
          style={{ marginTop: "30px" }}
          pagination={getPaginationProps()}
        />
      </div>
      {showKeyModal && (
        <ShowApiKeyModal
          open={showKeyModal}
          onClose={() => {
            setShowKeyModal(false);
            setCurrentApiKey("");
          }}
          apiKey={currentApiKey}
        />
      )}
      <ConfigProvider
        theme={{
          token: {
            colorBgBase: "#1A1D38"
          },
          components: {
            Modal: {
              contentBg: "#1A1D38",
              colorText: "#fff"
            },
            Radio: {
              colorBorder: "#fff",
              controlOutline: "#fff"
            }
          }
        }}>
        <Modal
          footer={null}
          open={isModalOpen}
          closable={false}
          centered
          // className={classes.createJobPopup}
        >
          <Row justify="space-between">
            <h1>Create API Key</h1>
            <CloseOutlined
              style={{ color: "#fff" }}
              onClick={() => {
                setIsModalOpen(false);
              }}
              className={classes.ModalCloseIcon}
            />
          </Row>
          <Form
            ref={form}
            className={classes.form}
            layout="vertical"
            onFinish={async () => {
              setIsLoading(true);
              const res = await createApiKey(form.current?.getFieldsValue());
              setIsLoading(false);
              if (!res || (res instanceof Array && res.length === 0)) return;

              await fetchAPIKeys();

              setIsModalOpen(false);

              message.success("Api Key created successfully");
              setShowKeyModal(true);
              setCurrentApiKey(res.key);
              form.current?.resetFields();
            }}>
            <Form.Item name="clientName" label="Client Name" rules={[{ required: true }]}>
              <Input placeholder="ABC" className={classes.nameInput} name="clientName" />
            </Form.Item>
            <Form.Item name="clientEmail" label="Client Email" rules={[{ required: true }]}>
              <Input placeholder="qbc@abc.abc" className={classes.nameInput} name="clientEmail" />
            </Form.Item>
          </Form>
          <Button
            className={classes.ProceedButton}
            onClick={() => {
              form.current?.submit();
            }}
            loading={isLoading}
            disabled={isLoading}>
            Create API Key
          </Button>
        </Modal>

        <Modal
          open={modal1Open}
          onCancel={() => setModal1Open(false)}
          centered
          footer={null}
          destroyOnClose
          closeIcon={<img src={cancelIcon} alt="cancel-icon" style={{ padding: "5px" }} />}>
          <Row justify={"space-between"}>
            <h1 className={classes.modalTitle}>Edit API Key</h1>
          </Row>

          <Form layout="vertical">
            <Form.Item label="Client Name">
              <Input
                className={classes.name}
                placeholder="ABC"
                value={selected.clientName}
                onChange={(e) => {
                  setSelected({
                    ...selected,
                    clientName: e.target.value
                  });
                }}
                disabled
              />
            </Form.Item>
            <Form.Item label="Client Email">
              <Input
                className={classes.name}
                placeholder="abc@abc.abc"
                value={selected.clientEmail}
                onChange={(e) => {
                  setSelected({
                    ...selected,
                    clientEmail: e.target.value
                  });
                }}
                disabled
              />
            </Form.Item>

            <>
              <Form.Item label="Block API key?">
                <Row>
                  <Radio.Group
                    onChange={(e) => {
                      setSelected({
                        ...selected,
                        isBlocked: e.target.value
                      });
                    }}
                    value={selected.isBlocked}>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Row>
              </Form.Item>
            </>

            <Button
              htmlType="submit"
              className={classes.ConfirmButton}
              loading={isLoading}
              disabled={isLoading}
              onClick={async () => {
                setIsLoading(true);
                const res = await updateApiKey(selected.id, selected);
                if (!res) {
                  setIsLoading(false);
                  return;
                }
                await fetchAPIKeys();
                message.success("Api Key updated Successfully!");
                setIsLoading(false);
                setModal1Open(false);
              }}>
              Confirm
            </Button>
          </Form>
        </Modal>
        <ConfigProvider
          theme={{
            token: {
              colorBgBase: "#202440"
            },
            components: {
              Modal: {
                contentBg: "#202440",
                colorText: "#fff"
              },
              InputNumber: {
                colorText: "white",
                colorIcon: "white",
                addonBg: "#0D111C",
                colorPrimary: "#fff"
              },
              Select: {
                colorText: "#fff"
              }
            }
          }}>
          {addCreditsModal && (
            <AddCreditsForUserModal
              users={tableData.map((it: { user: User }) => it.user) as any}
              onClose={() => setAddCreditsModal(false)}
              onSuccess={async () => {
                await fetchAPIKeys();
              }}
            />
          )}
        </ConfigProvider>
      </ConfigProvider>
    </>
  );
};
